import { type Selection } from 'react-aria-components'
import {
  InputShellRoot,
  InputShellControl,
  InputShellSlot,
} from '@/components/inputs/InputShell'
import { InputChip, InputChipList } from '@/components/Chip'
import {
  type PointerEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { IconButton } from '@/components/IconButton'
import { useElementSize } from '@/hooks/element-hooks'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { SelectOptionsPicker } from './SelectOptionsPicker'
import styles from './MultiSelect.module.css'

export interface MultiSelectProps<T extends object> {
  /**
   * Label for the combo box
   */
  'aria-label'?: string

  /**
   * ID of the element to use as a label for the combo box.
   */
  'aria-labelledby'?: string

  /**
   * ID of the element to use as a hint for the combo box.
   */
  'aria-describedby'?: string

  /**
   * ID for the field.
   */
  id?: string

  /**
   * Items to display in the combo box
   */
  items: T[]

  /**
   * Currently selected keys
   */
  selectedKeys: Set<string>

  /**
   * Keys that should be disabled.
   */
  disabledKeys?: Set<string>

  /**
   * Called when selection changes
   */
  onSelectionChange: (keys: Set<string>) => void

  /**
   * Function to get a unique key for an item
   */
  getKey: (item: T) => string

  /**
   * Function to get the display name for an item
   */
  getLabel: (item: T) => string

  /**
   * Whether the combo box is disabled
   */
  isDisabled?: boolean

  /**
   * Whether the combo box is in an invalid state
   */
  invalid?: boolean

  /**
   * Placeholder to use in the input when there are no selected items.
   */
  placeholder?: string

  /**
   * Placeholder to use in the search field.
   */
  searchPlaceholder?: string

  /**
   * Filter function to use in the search field instead of the default.
   */
  filter?: (textValue: string, inputValue: string) => boolean

  /**
   * Marks the element as private, excluding its content from
   * session recordings.
   */
  'data-private'?: boolean

  /**
   * Test ID attribute prefix for the multi-select's elements.
   *
   * The multi-select is a complex component but works more or less
   * the same way for every consumer. The test author is likely to wrap it
   * in a reusable module that just needs a prefix to target a specific
   * instance of the multi-select.
   */
  testId?: string
}

/**
 * A multi-select component that allows users to select multiple items from a list.
 */
export function MultiSelect<T extends object>({
  items,
  selectedKeys,
  onSelectionChange,
  getKey,
  getLabel,
  isDisabled,
  invalid,
  placeholder,
  searchPlaceholder,
  filter,
  disabledKeys,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'data-private': dataPrivate,
  testId,
}: MultiSelectProps<T>) {
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const open = useCallback(() => setIsOpen(true), [setIsOpen])
  const toggleOpen = useCallback(() => setIsOpen((v) => !v), [setIsOpen])

  const rootRef = useRef<HTMLDivElement>(null)
  const toggleButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)

  const itemsMap = useMemo(() => {
    const map = new Map<string, T>()
    for (const item of items) {
      map.set(getKey(item), item)
    }
    return map
  }, [items, getKey])

  const selectedItems = useMemo(() => {
    const selected: T[] = []
    for (const key of selectedKeys) {
      const item = itemsMap.get(key)
      if (item) {
        selected.push(item)
      }
    }
    return selected
  }, [itemsMap, selectedKeys])

  const hasSelectedItems = selectedItems.length > 0

  const openOnInputPress = useCallback(
    (evt: PointerEvent) => {
      // Don't open if the event was triggered on a button
      let element: Element | null = evt.target as Element
      do {
        if (!element) {
          return
        }

        if (element.role === 'row' || element.tagName === 'BUTTON') {
          return
        }

        element = element.parentElement
      } while (element)

      if (toggleButtonRef.current) {
        // Focus the toggle button so when the user closes the menu via the keyboard,
        // focus is restored to the toggle button.
        toggleButtonRef.current.focus()
      }

      toggleOpen()
    },
    [toggleOpen],
  )

  const handleSelectionChange = useCallback(
    (selection: Selection) => {
      if (selection === 'all') {
        onSelectionChange(new Set(items.map((i) => getKey(i))))
      } else {
        const set = new Set<string>()
        for (const item of selection) {
          set.add(item.toString())
        }
        onSelectionChange(set)
      }
    },
    [onSelectionChange, items, getKey],
  )

  const handleRemove = useCallback(
    (keys: Set<string>) => {
      const newKeys = new Set(selectedKeys)
      keys.forEach((k) => newKeys.delete(k))
      onSelectionChange(newKeys)
    },
    [selectedKeys, onSelectionChange],
  )

  const { size, connectRef } = useElementSize()

  const mergedRef = useComposedRefs(rootRef, connectRef)

  return (
    <>
      <InputShellRoot
        ref={mergedRef}
        isDisabled={isDisabled}
        invalid={invalid}
        onPointerUp={openOnInputPress}
        data-private={dataPrivate}
        testId={testId}
      >
        <InputShellControl pad={!hasSelectedItems}>
          {selectedItems.length === 0 ? (
            <div className={styles.placeholder}>{placeholder}</div>
          ) : (
            <div className={styles.chipsContainer}>
              <InputChipList
                isDisabled={isDisabled}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                onRequestMenuOpen={open}
                onRemove={handleRemove}
                disabledKeys={disabledKeys}
              >
                {selectedItems.map((item) => {
                  const key = getKey(item)
                  return (
                    <InputChip
                      key={key}
                      id={key}
                      text={getLabel(item)}
                      testId={`${testId}-multi-select-selected-item-${key}`}
                      removeButtonTestId={`${testId}-multi-select-selected-item-remove-button-${key}`}
                    />
                  )
                })}
              </InputChipList>
            </div>
          )}
        </InputShellControl>
        <InputShellSlot side="right">
          <IconButton
            ref={toggleButtonRef}
            name="chevron"
            label="Toggle options menu"
            variant="secondary"
            onPress={toggleOpen}
            rotate={90}
            id={id}
            testId={`${testId}-multi-select-options-menu-toggle`}
          />
        </InputShellSlot>
      </InputShellRoot>

      <SelectOptionsPicker
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        isMultiSelect
        disabledKeys={disabledKeys}
        // At the moment, the list is always searchable
        // because at the moment, the only way to get focus management for the list
        // is to have an autocomplete with a search field.
        // https://github.com/adobe/react-spectrum/discussions/7999
        isSearchable
        triggerRef={rootRef}
        triggerWidth={size.width}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        searchInputValue={inputValue}
        onSearchInputValueChange={setInputValue}
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        items={items}
        getKey={getKey}
        getLabel={getLabel}
        searchPlaceholder={searchPlaceholder}
        filter={filter}
        searchInputTestId={testId ? `${testId}-search-input` : undefined}
        clearSearchButtonTestId={
          testId ? `${testId}-clear-search-button` : undefined
        }
        closeMenuButtonTestId={
          testId ? `${testId}-close-menu-button` : undefined
        }
        itemTestId={testId ? `${testId}-multi-select-option-[key]` : undefined}
      />
    </>
  )
}
